import React, { useState } from "react";

// Tab Navigation Component
const TabNavigation = ({ activeTab, setActiveTab, tabs }) => {
	return (
		<div className="tab-navigation">
			{tabs.map((tab) => (
				<button
					key={tab.id}
					className={`tab-button ${activeTab === tab.id ? "active" : ""}`}
					onClick={() => setActiveTab(tab.id)}
					style={{
						background: "none",
						border: "none",
						padding: "16px 24px",
						cursor: "pointer",
						fontSize: "14px",
						fontWeight: "500",
						color: activeTab === tab.id ? "#0076BD" : "#6b7280",
						borderBottom:
							activeTab === tab.id
								? "2px solid #0076BD"
								: "2px solid transparent",
						transition: "all 0.2s ease",
						whiteSpace: "nowrap",
					}}
				>
					{tab.label}
				</button>
			))}
		</div>
	);
};

// Export Dropdown Component - Updated to use calculated data fields
const ExportDropdown = ({ setData }) => {
	const [isOpen, setIsOpen] = useState(false);

	const exportSummarized = () => {
		const headers = [
			"App Name",
			"Developer",
			"Category",
			"Downloads",
			"Rating",
			"CTR",
			"VTR",
			"Viewability",
		];
		const csvContent = [
			headers.join(","),
			...setData.apps.map((app) => {
				const appName = app.app_name || app.name || "Unknown App";
				const appMetrics = app.metrics || {};

				return [
					`"${appName}"`, // Quote to handle commas in names
					`"${app.developer || "N/A"}"`,
					`"${app.category || "N/A"}"`,
					app.downloads || "N/A",
					app.rating || "N/A",
					appMetrics.rawCtr ? `${appMetrics.rawCtr.toFixed(3)}%` : "N/A",
					appMetrics.rawVtr ? `${appMetrics.rawVtr.toFixed(1)}%` : "N/A",
					appMetrics.viewability || "N/A",
				].join(",");
			}),
		].join("\n");

		downloadCSV(
			csvContent,
			`${setData.name.replace(/\s+/g, "_")}_summarized.csv`,
		);
		setIsOpen(false);
	};

	const exportDetailed = () => {
		const headers = [
			"App Name",
			"Developer",
			"Category",
			"Platform",
			"Downloads",
			"Rating",
			"CTR",
			"VTR",
			"Viewability",
			"Impressions",
			"Revenue/Imp",
			"Market Requests",
			"Bundle ID",
		];
		const csvContent = [
			headers.join(","),
			...setData.apps.map((app) => {
				const appName = app.app_name || app.name || "Unknown App";
				const appMetrics = app.metrics || {};

				return [
					`"${appName}"`,
					`"${app.developer || "N/A"}"`,
					`"${app.category || "N/A"}"`,
					`"${app.platform || "N/A"}"`,
					app.downloads || "N/A",
					app.rating || "N/A",
					appMetrics.rawCtr ? `${appMetrics.rawCtr.toFixed(3)}%` : "N/A",
					appMetrics.rawVtr ? `${appMetrics.rawVtr.toFixed(1)}%` : "N/A",
					appMetrics.viewability || "N/A",
					appMetrics.impressions || "N/A",
					app.avgRevenuePerImp || appMetrics.revenuePerImp || "N/A",
					app.totalCprMarketRequests || "N/A",
					app.bundle_id || "N/A",
				].join(",");
			}),
		].join("\n");

		downloadCSV(
			csvContent,
			`${setData.name.replace(/\s+/g, "_")}_detailed.csv`,
		);
		setIsOpen(false);
	};

	const downloadCSV = (content, filename) => {
		const blob = new Blob([content], { type: "text/csv" });
		const url = window.URL.createObjectURL(blob);
		const a = document.createElement("a");
		a.href = url;
		a.download = filename;
		a.click();
		window.URL.revokeObjectURL(url);
	};

	return (
		<div className="export-dropdown" style={{ position: "relative" }}>
			<button
				onClick={() => setIsOpen(!isOpen)}
				style={{
					display: "flex",
					alignItems: "center",
					gap: "8px",
					padding: "8px 16px",
					backgroundColor: "#3b82f6",
					color: "white",
					border: "none",
					borderRadius: "6px",
					fontSize: "14px",
					cursor: "pointer",
				}}
			>
				Export Set
				<svg
					width="16"
					height="16"
					viewBox="0 0 24 24"
					fill="none"
					stroke="currentColor"
					strokeWidth="2"
				>
					<polyline points="6,9 12,15 18,9"></polyline>
				</svg>
			</button>

			{isOpen && (
				<div
					style={{
						position: "absolute",
						top: "100%",
						right: 0,
						backgroundColor: "white",
						border: "1px solid #e5e7eb",
						borderRadius: "4px",
						boxShadow: "0 2px 8px rgba(0, 0, 0, 0.1)",
						zIndex: 1000,
						minWidth: "160px",
						marginTop: "4px",
					}}
				>
					<button
						onClick={exportSummarized}
						style={{
							width: "100%",
							padding: "8px 12px",
							border: "none",
							background: "none",
							textAlign: "left",
							cursor: "pointer",
							fontSize: "14px",
							color: "#374151",
						}}
						onMouseEnter={(e) => (e.target.style.backgroundColor = "#f9fafb")}
						onMouseLeave={(e) =>
							(e.target.style.backgroundColor = "transparent")
						}
					>
						Export Summarized
					</button>
					<button
						onClick={exportDetailed}
						style={{
							width: "100%",
							padding: "8px 12px",
							border: "none",
							background: "none",
							textAlign: "left",
							cursor: "pointer",
							fontSize: "14px",
							color: "#374151",
						}}
						onMouseEnter={(e) => (e.target.style.backgroundColor = "#f9fafb")}
						onMouseLeave={(e) =>
							(e.target.style.backgroundColor = "transparent")
						}
					>
						Export Detailed
					</button>
				</div>
			)}
		</div>
	);
};

// Overview Tab Component - Updated to use aggregated metrics from calculator
const OverviewTab = ({ setData }) => {
	const totalApps = setData.apps.length;

	// Total downloads - sum from individual apps
	const totalDownloads = setData.apps.reduce(
		(sum, app) => sum + (app.downloads || 0),
		0,
	);

	// Total impressions - use from individual apps
	const totalImpressions = setData.apps.reduce((sum, app) => {
		const appMetrics = app.metrics || {};
		return sum + (appMetrics.impressions || app.totalImpressions || 0);
	}, 0);

	// Average rating calculation
	const appsWithRating = setData.apps.filter(
		(app) => app.rating && app.rating > 0,
	);
	const avgRating =
		appsWithRating.length > 0
			? appsWithRating.reduce((sum, app) => sum + app.rating, 0) /
				appsWithRating.length
			: 0;

	// Use aggregated metrics from calculator instead of individual app averages
	const avgCTR =
		setData.metrics?.ctr?.value || setData.metrics?.avgCtr || "N/A";
	const avgViewability =
		setData.metrics?.viewability?.value ||
		setData.metrics?.avgViewability ||
		"N/A";

	// Parse numeric values for display (remove % and $ symbols)
	const parseMetricValue = (value) => {
		if (!value || value === "N/A") return 0;
		if (typeof value === "string") {
			return parseFloat(value.replace(/[%$]/g, ""));
		}
		return value;
	};

	const formatNumber = (num) => {
		if (num >= 1000000) return (num / 1000000).toFixed(1) + "M";
		if (num >= 1000) return (num / 1000).toFixed(1) + "K";
		return num.toString();
	};

	return (
		<div className="overview-tab" style={{ padding: "24px" }}>
			<div
				style={{
					display: "grid",
					gridTemplateColumns: "1fr 1fr",
					gap: "32px",
					marginBottom: "24px",
				}}
			>
				<div className="overview-section" style={{ background: "white" }}>
					<h3
						style={{
							fontSize: "16px",
							fontWeight: "500",
							color: "#1f2937",
							margin: "0 0 20px 0",
							paddingBottom: "8px",
							borderBottom: "1px solid #e5e7eb",
						}}
					>
						Performance Metrics
					</h3>
					<div
						style={{
							display: "grid",
							gridTemplateColumns: "1fr",
							gap: "12px",
						}}
					>
						<div
							style={{
								display: "flex",
								justifyContent: "space-between",
								alignItems: "center",
								padding: "8px 0",
							}}
						>
							<div
								style={{
									fontSize: "12px",
									color: "#6b7280",
									textTransform: "uppercase",
									fontWeight: "400",
									letterSpacing: "0.5px",
								}}
							>
								Total Downloads
							</div>
							<div
								style={{
									fontSize: "15px",
									fontWeight: "600",
									color: "#1f2937",
								}}
							>
								{formatNumber(totalDownloads)}
							</div>
						</div>
						<div
							style={{
								display: "flex",
								justifyContent: "space-between",
								alignItems: "center",
								padding: "8px 0",
							}}
						>
							<div
								style={{
									fontSize: "12px",
									color: "#6b7280",
									textTransform: "uppercase",
									fontWeight: "400",
									letterSpacing: "0.5px",
								}}
							>
								Average Rating
							</div>
							<div
								style={{
									fontSize: "15px",
									fontWeight: "600",
									color: "#1f2937",
								}}
							>
								{avgRating > 0 ? `${avgRating.toFixed(1)} ★` : "N/A"}
							</div>
						</div>
						<div
							style={{
								display: "flex",
								justifyContent: "space-between",
								alignItems: "center",
								padding: "8px 0",
							}}
						>
							<div
								style={{
									fontSize: "12px",
									color: "#6b7280",
									textTransform: "uppercase",
									fontWeight: "400",
									letterSpacing: "0.5px",
								}}
							>
								Average CTR
							</div>
							<div
								style={{
									fontSize: "15px",
									fontWeight: "600",
									color: "#1f2937",
								}}
							>
								{avgCTR}
							</div>
						</div>
						<div
							style={{
								display: "flex",
								justifyContent: "space-between",
								alignItems: "center",
								padding: "8px 0",
							}}
						>
							<div
								style={{
									fontSize: "12px",
									color: "#6b7280",
									textTransform: "uppercase",
									fontWeight: "400",
									letterSpacing: "0.5px",
								}}
							>
								Average Viewability
							</div>
							<div
								style={{
									fontSize: "15px",
									fontWeight: "600",
									color: "#1f2937",
								}}
							>
								{avgViewability}
							</div>
						</div>
						<div
							style={{
								display: "flex",
								justifyContent: "space-between",
								alignItems: "center",
								padding: "8px 0",
							}}
						>
							<div
								style={{
									fontSize: "12px",
									color: "#6b7280",
									textTransform: "uppercase",
									fontWeight: "400",
									letterSpacing: "0.5px",
								}}
							>
								Total Impressions
							</div>
							<div
								style={{
									fontSize: "15px",
									fontWeight: "600",
									color: "#1f2937",
								}}
							>
								{formatNumber(totalImpressions)}
							</div>
						</div>
					</div>
				</div>

				<div className="overview-section" style={{ background: "white" }}>
					<h3
						style={{
							fontSize: "16px",
							fontWeight: "500",
							color: "#1f2937",
							margin: "0 0 20px 0",
							paddingBottom: "8px",
							borderBottom: "1px solid #e5e7eb",
						}}
					>
						Platform Coverage
					</h3>
					<div
						style={{
							display: "grid",
							gridTemplateColumns: "1fr auto",
							gap: "12px",
							alignItems: "center",
						}}
					>
						<div
							style={{
								display: "contents",
								fontWeight: "500",
								color: "#374151",
								fontSize: "12px",
								textTransform: "uppercase",
								letterSpacing: "0.5px",
							}}
						>
							<span>Platform</span>
							<span>Apps</span>
						</div>
						{["iOS", "Android", "Web"].map((platform) => {
							const platformCount = setData.apps.filter(
								(app) =>
									app.platform &&
									app.platform.toLowerCase().includes(platform.toLowerCase()),
							).length;
							return (
								<React.Fragment key={platform}>
									<span
										style={{
											fontSize: "13px",
											color: "#1f2937",
											padding: "8px 0",
											fontWeight: "400",
										}}
									>
										{platform}
									</span>
									<span
										style={{
											color: "#374151",
											fontWeight: "500",
											fontSize: "13px",
										}}
									>
										{platformCount}
									</span>
								</React.Fragment>
							);
						})}
					</div>
				</div>
			</div>

			<div
				style={{
					backgroundColor: "#f9fafb",
					border: "1px solid #e5e7eb",
					borderRadius: "8px",
					padding: "16px",
				}}
			>
				<div
					style={{
						display: "flex",
						justifyContent: "space-around",
						gap: "24px",
					}}
				>
					<div
						style={{
							display: "flex",
							flexDirection: "column",
							alignItems: "center",
							gap: "4px",
						}}
					>
						<span
							style={{
								fontSize: "11px",
								color: "#6b7280",
								textTransform: "uppercase",
								fontWeight: "400",
							}}
						>
							Apps
						</span>
						<span
							style={{
								fontSize: "16px",
								fontWeight: "600",
								color: "#1f2937",
							}}
						>
							{totalApps}
						</span>
					</div>
					<div
						style={{
							display: "flex",
							flexDirection: "column",
							alignItems: "center",
							gap: "4px",
						}}
					>
						<span
							style={{
								fontSize: "11px",
								color: "#6b7280",
								textTransform: "uppercase",
								fontWeight: "400",
							}}
						>
							Downloads
						</span>
						<span
							style={{
								fontSize: "16px",
								fontWeight: "600",
								color: "#1f2937",
							}}
						>
							{formatNumber(totalDownloads)}
						</span>
					</div>
					<div
						style={{
							display: "flex",
							flexDirection: "column",
							alignItems: "center",
							gap: "4px",
						}}
					>
						<span
							style={{
								fontSize: "11px",
								color: "#6b7280",
								textTransform: "uppercase",
								fontWeight: "400",
							}}
						>
							Avg Rating
						</span>
						<span
							style={{
								fontSize: "16px",
								fontWeight: "600",
								color: "#1f2937",
							}}
						>
							{avgRating > 0 ? `${avgRating.toFixed(1)} ★` : "N/A"}
						</span>
					</div>
				</div>
			</div>
		</div>
	);
};

// Apps Tab Component - Updated to use calculated data
const AppsTab = ({ setData }) => {
	const [currentPage, setCurrentPage] = useState(1);
	const [searchTerm, setSearchTerm] = useState("");
	const appsPerPage = 10;

	// Reset page when search changes
	React.useEffect(() => {
		setCurrentPage(1);
	}, [searchTerm]);

	// Filter apps based on search term
	const filteredApps = setData.apps.filter((app) => {
		const appName = app.app_name || app.name || "";
		const developer = app.developer || "";
		const category = app.category || "";
		const searchLower = searchTerm.toLowerCase();

		return (
			appName.toLowerCase().includes(searchLower) ||
			developer.toLowerCase().includes(searchLower) ||
			category.toLowerCase().includes(searchLower)
		);
	});

	// Pagination
	const totalPages = Math.ceil(filteredApps.length / appsPerPage);
	const startIndex = (currentPage - 1) * appsPerPage;
	const endIndex = startIndex + appsPerPage;
	const currentApps = filteredApps.slice(startIndex, endIndex);

	// Format functions
	const formatNumber = (value) => {
		if (!value) return "N/A";
		if (typeof value === "number") {
			if (value >= 1000000) {
				return (value / 1000000).toFixed(1) + "M";
			} else if (value >= 1000) {
				return (value / 1000).toFixed(1) + "K";
			}
			return value.toLocaleString();
		}
		return value;
	};

	const formatPercentage = (value) => {
		if (!value) return "N/A";
		if (typeof value === "string" && value.includes("%")) return value;
		return typeof value === "string" ? value : `${value.toFixed(2)}%`;
	};

	return (
		<div className="apps-tab" style={{ padding: "24px" }}>
			{/* Search */}
			<div style={{ marginBottom: "16px", maxWidth: "300px" }}>
				<input
					type="text"
					placeholder="Search apps..."
					value={searchTerm}
					onChange={(e) => setSearchTerm(e.target.value)}
					style={{
						width: "100%",
						padding: "8px 12px",
						border: "1px solid #d1d5db",
						borderRadius: "6px",
						fontSize: "14px",
					}}
				/>
			</div>

			{/* Table Header */}
			<div
				style={{
					display: "grid",
					gridTemplateColumns: "2fr 1.5fr 1fr 1fr 1fr 1fr 1fr",
					gap: "16px",
					alignItems: "center",
					padding: "16px 8px",
					fontWeight: "500",
					color: "#374151",
					backgroundColor: "#f9fafb",
					borderBottom: "1px solid #e5e7eb",
					fontSize: "12px",
				}}
			>
				<div>App Name</div>
				<div>Developer</div>
				<div>Downloads</div>
				<div>Rating</div>
				<div>CTR</div>
				<div>Viewability</div>
				<div>Impressions</div>
			</div>

			{/* Table Rows */}
			<div>
				{currentApps.map((app, index) => {
					const appName = app.app_name || app.name || "Unknown App";
					const appMetrics = app.metrics || {};

					return (
						<div
							key={index}
							style={{
								display: "grid",
								gridTemplateColumns: "2fr 1.5fr 1fr 1fr 1fr 1fr 1fr",
								gap: "16px",
								alignItems: "center",
								padding: "12px 8px",
								borderBottom: "1px solid #f3f4f6",
								backgroundColor: index % 2 === 0 ? "#f9fafb" : "transparent",
							}}
						>
							<div
								style={{
									fontSize: "13px",
									fontWeight: "400",
									color: "#1f2937",
								}}
							>
								<div style={{ fontWeight: "500" }}>{appName}</div>
								{app.platform && (
									<div
										style={{
											fontSize: "11px",
											color: "#6b7280",
											marginTop: "2px",
										}}
									>
										{app.platform}
									</div>
								)}
							</div>
							<div style={{ fontSize: "13px", color: "#6b7280" }}>
								{app.developer || "N/A"}
							</div>
							<div style={{ fontSize: "13px" }}>
								{formatNumber(app.downloads)}
							</div>
							<div style={{ fontSize: "13px" }}>
								{app.rating ? `★ ${app.rating.toFixed(1)}` : "N/A"}
							</div>
							<div style={{ fontSize: "13px" }}>
								{appMetrics.rawCtr !== null && appMetrics.rawCtr !== undefined
									? `${appMetrics.rawCtr.toFixed(4)}%`
									: "N/A"}
							</div>
							<div style={{ fontSize: "13px" }}>
								{formatPercentage(appMetrics.viewability)}
							</div>
							<div style={{ fontSize: "13px" }}>
								{formatNumber(appMetrics.impressions || app.totalImpressions)}
							</div>
						</div>
					);
				})}
			</div>

			{/* Pagination */}
			{totalPages > 1 && (
				<div
					style={{
						padding: "16px 0",
						display: "flex",
						justifyContent: "center",
						alignItems: "center",
						gap: "8px",
					}}
				>
					<button
						onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
						disabled={currentPage === 1}
						style={{
							padding: "6px 12px",
							border: "1px solid #d1d5db",
							backgroundColor: currentPage === 1 ? "#f9fafb" : "white",
							color: currentPage === 1 ? "#9ca3af" : "#374151",
							borderRadius: "4px",
							cursor: currentPage === 1 ? "not-allowed" : "pointer",
							fontSize: "14px",
						}}
					>
						Previous
					</button>

					<span
						style={{
							padding: "6px 12px",
							fontSize: "14px",
							color: "#6b7280",
						}}
					>
						{`Page ${currentPage} of ${totalPages}`}
					</span>

					<button
						onClick={() =>
							setCurrentPage(Math.min(totalPages, currentPage + 1))
						}
						disabled={currentPage === totalPages}
						style={{
							padding: "6px 12px",
							border: "1px solid #d1d5db",
							backgroundColor: currentPage === totalPages ? "#f9fafb" : "white",
							color: currentPage === totalPages ? "#9ca3af" : "#374151",
							borderRadius: "4px",
							cursor: currentPage === totalPages ? "not-allowed" : "pointer",
							fontSize: "14px",
						}}
					>
						Next
					</button>
				</div>
			)}

			{/* Results info */}
			<div
				style={{
					padding: "12px 0",
					fontSize: "12px",
					color: "#6b7280",
					textAlign: "center",
				}}
			>
				Showing {startIndex + 1}-{Math.min(endIndex, filteredApps.length)} of{" "}
				{filteredApps.length} apps
				{searchTerm && ` (filtered from ${setData.apps.length} total)`}
			</div>
		</div>
	);
};

// Categories Tab Component - Updated to handle calculated categories
const CategoriesTab = ({ setData }) => {
	// Group apps by category
	const categoryData = setData.apps.reduce((acc, app) => {
		// Handle both single category and categories array
		let categories = [];
		if (app.metrics?.categories && Array.isArray(app.metrics.categories)) {
			categories = app.metrics.categories;
		} else if (app.category) {
			categories = [app.category];
		} else {
			categories = ["Uncategorized"];
		}

		categories.forEach((category) => {
			const catName = category || "Uncategorized";
			if (!acc[catName]) {
				acc[catName] = 0;
			}
			acc[catName]++;
		});

		return acc;
	}, {});

	const totalApps = setData.apps.length;
	const categories = Object.entries(categoryData)
		.map(([name, count]) => ({
			name,
			count,
			percentage: ((count / totalApps) * 100).toFixed(1),
		}))
		.sort((a, b) => b.count - a.count);

	return (
		<div className="categories-tab" style={{ padding: "24px" }}>
			<div
				style={{
					display: "grid",
					gridTemplateColumns: "2fr 1fr 1fr 2fr",
					gap: "16px",
					alignItems: "center",
					padding: "16px 8px",
					backgroundColor: "#f9fafb",
					borderBottom: "1px solid #e5e7eb",
					fontWeight: "500",
					color: "#374151",
					fontSize: "12px",
				}}
			>
				<div>Category</div>
				<div>Number of Apps</div>
				<div>Percentage</div>
				<div>Distribution</div>
			</div>

			<div style={{ maxHeight: "400px", overflowY: "auto" }}>
				{categories.map((category, index) => (
					<div
						key={index}
						style={{
							display: "grid",
							gridTemplateColumns: "2fr 1fr 1fr 2fr",
							gap: "16px",
							alignItems: "center",
							padding: "12px 8px",
							borderBottom: "1px solid #f3f4f6",
							backgroundColor: index % 2 === 0 ? "#f9fafb" : "transparent",
						}}
					>
						<div
							style={{
								fontWeight: "400",
								color: "#1f2937",
								fontSize: "13px",
							}}
						>
							{category.name}
						</div>
						<div
							style={{
								fontWeight: "500",
								color: "#374151",
								fontSize: "13px",
							}}
						>
							{category.count}
						</div>
						<div
							style={{
								color: "#6b7280",
								fontSize: "13px",
							}}
						>
							{category.percentage}%
						</div>
						<div>
							<div
								style={{
									width: "100%",
									height: "8px",
									backgroundColor: "#e5e7eb",
									borderRadius: "4px",
									overflow: "hidden",
								}}
							>
								<div
									style={{
										height: "100%",
										backgroundColor: "#0076BD",
										transition: "width 0.3s ease",
										width: `${category.percentage}%`,
									}}
								></div>
							</div>
						</div>
					</div>
				))}
			</div>
		</div>
	);
};

// Developers Tab Component - No changes needed, already correct
const DevelopersTab = ({ setData }) => {
	// Group apps by developer
	const developerData = setData.apps.reduce((acc, app) => {
		const developer = app.developer || "Unknown Developer";
		if (!acc[developer]) {
			acc[developer] = {
				name: developer,
				apps: 0,
				totalDownloads: 0,
				avgRating: 0,
				ratings: [],
			};
		}
		acc[developer].apps++;
		acc[developer].totalDownloads += app.downloads || 0;
		if (app.rating) {
			acc[developer].ratings.push(app.rating);
		}
		return acc;
	}, {});

	// Calculate average ratings
	const developers = Object.values(developerData)
		.map((dev) => ({
			...dev,
			avgRating:
				dev.ratings.length > 0
					? dev.ratings.reduce((sum, rating) => sum + rating, 0) /
						dev.ratings.length
					: 0,
		}))
		.sort((a, b) => b.apps - a.apps);

	const formatNumber = (num) => {
		if (num >= 1000000) return (num / 1000000).toFixed(1) + "M";
		if (num >= 1000) return (num / 1000).toFixed(1) + "K";
		return num.toString();
	};

	return (
		<div className="developers-tab" style={{ padding: "24px" }}>
			<div
				style={{
					display: "grid",
					gridTemplateColumns: "2fr 1fr 1.5fr 1fr",
					gap: "16px",
					alignItems: "center",
					padding: "16px 8px",
					backgroundColor: "#f9fafb",
					borderBottom: "1px solid #e5e7eb",
					fontWeight: "500",
					color: "#374151",
					fontSize: "12px",
				}}
			>
				<div>Developer</div>
				<div>No. of Apps</div>
				<div>Total Downloads</div>
				<div>Avg Rating</div>
			</div>

			<div style={{ maxHeight: "400px", overflowY: "auto" }}>
				{developers.map((developer, index) => (
					<div
						key={index}
						style={{
							display: "grid",
							gridTemplateColumns: "2fr 1fr 1.5fr 1fr",
							gap: "16px",
							alignItems: "center",
							padding: "12px 8px",
							borderBottom: "1px solid #f3f4f6",
							backgroundColor: index % 2 === 0 ? "#f9fafb" : "transparent",
						}}
					>
						<div
							style={{
								fontWeight: "400",
								color: "#1f2937",
								fontSize: "13px",
							}}
						>
							{developer.name}
						</div>
						<div
							style={{
								fontWeight: "500",
								color: "#374151",
								fontSize: "13px",
							}}
						>
							{developer.apps}
						</div>
						<div
							style={{
								color: "#6b7280",
								fontSize: "13px",
							}}
						>
							{formatNumber(developer.totalDownloads)}
						</div>
						<div
							style={{
								color: "#6b7280",
								fontSize: "13px",
							}}
						>
							{developer.avgRating > 0
								? `★ ${developer.avgRating.toFixed(1)}`
								: "N/A"}
						</div>
					</div>
				))}
			</div>
		</div>
	);
};

// Performance Tab Component - Updated to use calculated data
const PerformanceTab = ({ setData }) => {
	// Calculate performance metrics using calculated data
	const apps = setData.apps;
	const totalApps = apps.length;

	// CTR Performance - use calculated rawCtr
	const highCTRApps = apps.filter((app) => {
		const appMetrics = app.metrics || {};
		const ctr = appMetrics.rawCtr || 0;
		return ctr > 2; // 2% threshold
	}).length;

	// Viewability Performance - handle both string and number formats
	const highViewabilityApps = apps.filter((app) => {
		const appMetrics = app.metrics || {};
		const viewability = appMetrics.viewability;
		if (typeof viewability === "string") {
			return parseFloat(viewability.replace("%", "")) > 70;
		}
		return (viewability || 0) > 70;
	}).length;

	// Rating Performance
	const highRatingApps = apps.filter((app) => {
		return (app.rating || 0) > 4;
	}).length;

	const performanceData = [
		{
			metric: "High CTR (>2%)",
			apps: highCTRApps,
			percentage:
				totalApps > 0 ? Math.round((highCTRApps / totalApps) * 100) : 0,
		},
		{
			metric: "High Viewability (>70%)",
			apps: highViewabilityApps,
			percentage:
				totalApps > 0 ? Math.round((highViewabilityApps / totalApps) * 100) : 0,
		},
		{
			metric: "High Rating (>4.0)",
			apps: highRatingApps,
			percentage:
				totalApps > 0 ? Math.round((highRatingApps / totalApps) * 100) : 0,
		},
	];

	// Calculate overall performance score - use set's calculated performance score if available
	const performanceScore =
		setData.performanceScore ||
		Math.round(
			performanceData[0].percentage * 0.4 +
				performanceData[1].percentage * 0.4 +
				performanceData[2].percentage * 0.2,
		);

	return (
		<div className="performance-tab" style={{ padding: "24px" }}>
			<div
				style={{
					display: "flex",
					gap: "40px",
					alignItems: "flex-start",
				}}
			>
				{/* Performance Score Circle */}
				<div
					style={{
						flexShrink: 0,
						display: "flex",
						alignItems: "center",
						justifyContent: "center",
					}}
				>
					<div
						style={{
							width: "120px",
							height: "120px",
							borderRadius: "50%",
							background: "#0076BD",
							display: "flex",
							flexDirection: "column",
							alignItems: "center",
							justifyContent: "center",
							color: "white",
						}}
					>
						<div
							style={{
								fontSize: "32px",
								fontWeight: "600",
								lineHeight: 1,
							}}
						>
							{performanceScore}
						</div>
						<div
							style={{
								fontSize: "11px",
								fontWeight: "400",
								textTransform: "uppercase",
								letterSpacing: "0.5px",
								marginTop: "4px",
							}}
						>
							Performance Score
						</div>
					</div>
				</div>

				{/* Performance Metrics Table */}
				<div
					style={{
						flex: 1,
						minWidth: 0,
					}}
				>
					<div
						style={{
							display: "grid",
							gridTemplateColumns: "1.5fr 1fr 1fr 2fr",
							gap: "16px",
							alignItems: "center",
							padding: "16px 8px",
							backgroundColor: "#f9fafb",
							borderBottom: "1px solid #e5e7eb",
							fontWeight: "500",
							color: "#374151",
							fontSize: "12px",
							textTransform: "uppercase",
							letterSpacing: "0.5px",
						}}
					>
						<div>Performance Metric</div>
						<div>Apps</div>
						<div>Percentage</div>
						<div>Distribution</div>
					</div>

					<div style={{ background: "white" }}>
						{performanceData.map((item, index) => (
							<div
								key={index}
								style={{
									display: "grid",
									gridTemplateColumns: "1.5fr 1fr 1fr 2fr",
									gap: "16px",
									alignItems: "center",
									padding: "16px 8px",
									borderBottom: "1px solid #f3f4f6",
								}}
							>
								<div
									style={{
										fontSize: "13px",
										fontWeight: "400",
										color: "#1f2937",
									}}
								>
									{item.metric}
								</div>
								<div>
									<span
										style={{
											color: "white",
											padding: "4px 12px",
											borderRadius: "20px",
											fontWeight: "500",
											fontSize: "12px",
											display: "inline-block",
											minWidth: "24px",
											textAlign: "center",
											backgroundColor: "#000000",
										}}
									>
										{item.apps}
									</span>
								</div>
								<div
									style={{
										fontWeight: "500",
										color: "#374151",
										fontSize: "13px",
									}}
								>
									{item.percentage}%
								</div>
								<div style={{ padding: "0 8px" }}>
									<div
										style={{
											width: "100%",
											height: "8px",
											backgroundColor: "#e5e7eb",
											borderRadius: "4px",
											overflow: "hidden",
										}}
									>
										<div
											style={{
												height: "100%",
												borderRadius: "4px",
												transition: "width 0.3s ease",
												width: `${item.percentage}%`,
												backgroundColor: "#0076BD",
											}}
										></div>
									</div>
								</div>
							</div>
						))}
					</div>
				</div>
			</div>
		</div>
	);
};

const AppDetailsModal = ({ isOpen, onClose, selectedSet, prebuiltAppSets }) => {
	const [activeTab, setActiveTab] = useState("overview");

	if (!isOpen || !selectedSet || !prebuiltAppSets[selectedSet]) {
		return null;
	}

	const setData = prebuiltAppSets[selectedSet];

	// Tab configuration
	const tabs = [
		{ id: "overview", label: "Overview" },
		{ id: "app", label: "App" },
		{ id: "categories", label: "Categories" },
		{ id: "developers", label: "Developers" },
		{ id: "performance", label: "Performance" },
	];

	// Render tab content
	const renderTabContent = () => {
		switch (activeTab) {
			case "overview":
				return <OverviewTab setData={setData} />;
			case "app":
				return <AppsTab setData={setData} />;
			case "categories":
				return <CategoriesTab setData={setData} />;
			case "developers":
				return <DevelopersTab setData={setData} />;
			case "performance":
				return <PerformanceTab setData={setData} />;
			default:
				return <OverviewTab setData={setData} />;
		}
	};

	return (
		<>
			{/* Backdrop */}
			<div
				style={{
					position: "fixed",
					top: 0,
					left: 0,
					right: 0,
					bottom: 0,
					backgroundColor: "rgba(0, 0, 0, 0.5)",
					zIndex: 1000,
					display: "flex",
					alignItems: "center",
					justifyContent: "center",
					padding: "20px",
				}}
				onClick={onClose}
			>
				{/* Modal */}
				<div
					style={{
						backgroundColor: "white",
						borderRadius: "12px",
						width: "90%",
						maxWidth: "1000px",
						maxHeight: "90vh",
						overflow: "hidden",
						boxShadow:
							"0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)",
					}}
					onClick={(e) => e.stopPropagation()}
				>
					{/* Modal Header */}
					<div
						style={{
							position: "relative",
							padding: "24px 60px 24px 24px",
							borderBottom: "1px solid #e5e7eb",
							display: "flex",
							justifyContent: "space-between",
							alignItems: "center",
						}}
					>
						<div>
							<h2
								style={{
									fontSize: "20px",
									fontWeight: "600",
									color: "#1f2937",
									margin: "0 0 4px 0",
								}}
							>
								{setData.name}
							</h2>
							<p
								style={{
									fontSize: "14px",
									color: "#6b7280",
									margin: 0,
								}}
							>
								{setData.description}
							</p>
						</div>
						<div
							style={{
								display: "flex",
								alignItems: "center",
								gap: "12px",
								marginRight: "40px",
							}}
						>
							<ExportDropdown setData={setData} />
							<button
								onClick={onClose}
								style={{
									position: "absolute",
									top: "16px",
									right: "16px",
									background: "none",
									border: "none",
									cursor: "pointer",
									padding: "8px",
									borderRadius: "4px",
									color: "#6b7280",
									transition: "all 0.2s ease",
									zIndex: 10,
									fontSize: "24px",
									lineHeight: 1,
								}}
							>
								×
							</button>
						</div>
					</div>

					{/* Tab Navigation */}
					<TabNavigation
						activeTab={activeTab}
						setActiveTab={setActiveTab}
						tabs={tabs}
					/>

					{/* Modal Content */}
					<div
						style={{
							padding: 0,
							maxHeight: "60vh",
							overflowY: "auto",
						}}
					>
						{renderTabContent()}
					</div>
				</div>
			</div>
		</>
	);
};

export default AppDetailsModal;
